import { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useGetProjectsQuery } from '../../../generated/graphql';
import type { GetProjectsQueryData } from '../../../generated/graphql';

/**
 * The project fields the board needs to render an optimistic card the instant
 * it's added — name, version badge, and owner avatar. Sourced from the dialog's
 * own `getProjects` result rather than the Apollo cache, because `ProjectType`
 * uses `keyFields: false` and so cannot be read back by identity.
 */
export type AddProjectSelection = Pick<
  GetProjectsQueryData['projects'][number],
  '_id' | 'name' | 'versionCount' | 'ownerUser'
>;

interface AddProjectToBoardDialogProps {
  open: boolean;
  onClose: () => void;
  excludeProjectIds: string[];
  onSubmit: (project: AddProjectSelection) => void;
}

export function AddProjectToBoardDialog({
  open,
  onClose,
  excludeProjectIds,
  onSubmit,
}: AddProjectToBoardDialogProps) {
  const { data, loading, error } = useGetProjectsQuery();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
    }
  }, [open]);

  const excludeSet = useMemo(
    () => new Set(excludeProjectIds),
    [excludeProjectIds]
  );
  const available = useMemo(
    () => (data?.projects ?? []).filter((p) => !excludeSet.has(p._id)),
    [data, excludeSet]
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add project to board</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            We couldn't load your projects. Try refreshing.
          </Alert>
        )}
        {loading && <CircularProgress />}
        {!loading && !error && available.length === 0 && (
          <Typography color="text.secondary">
            No projects available — all of your projects are already on this
            board.
          </Typography>
        )}
        <List>
          {available.map((p) => (
            <ListItemButton
              key={p._id}
              selected={selectedId === p._id}
              onClick={() => setSelectedId(p._id)}
            >
              <ListItemText primary={p.name} />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedId}
          onClick={() => {
            const selected = available.find((p) => p._id === selectedId);
            if (selected) {
              onSubmit({
                _id: selected._id,
                name: selected.name,
                versionCount: selected.versionCount,
                ownerUser: selected.ownerUser,
              });
              setSelectedId(null);
            }
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
